import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import {
  CheckCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { d506Api, PendingItem } from '../../services/d506Api';
import { useD506Approval } from './hooks/useD506Approval';
import { BatchAcceptanceBridge, pwd506f1AcceptanceManifest } from '../../features/acceptance';

const { Title, Text } = Typography;

// ============================================================
// Helpers
// ============================================================

function renderStatus(stat: string): React.ReactNode {
  if (!stat || stat === '') return <Tag color="default">未核准</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  return <Tag color="orange">{stat}</Tag>;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('zh-TW');
  } catch {
    return dateStr;
  }
}

function formatAmount(value: number): string {
  if (!value && value !== 0) return '—';
  return value.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ============================================================
// Main Component
// ============================================================

const D506Approval: React.FC = () => {
  const {
    loading,
    approving,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
  } = useD506Approval();

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<PendingItem> = [
    {
      title: <span data-contract-field="fd160_01">退出單號</span>,
      dataIndex: 'doc_no',
      key: 'doc_no',
      width: 110,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="f104_12">廠商名稱</span>,
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      ellipsis: true,
      render: (text: string, record: PendingItem) =>
        text || record.vendor_code || '—',
    },
    {
      title: <span data-contract-field="fd160_11">繳庫單號</span>,
      dataIndex: 'receiving_doc_no',
      key: 'receiving_doc_no',
      width: 120,
      render: (text: string) => text || '—',
    },
    {
      title: <span data-contract-field="fd160_12">退回日期</span>,
      dataIndex: 'return_date',
      key: 'return_date',
      width: 110,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: <span data-contract-field="fd160_16">退出金額</span>,
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (v: number) => formatAmount(v),
    },
    {
      title: <span data-contract-field="fd160_80">狀態</span>,
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter form
  // ============================================================
  const handleSearch = () => { fetchPending(); };

  const handleFilterChange = (field: 'docStart' | 'docEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>繳庫退出單核准 (pwd506f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="fd160_01">退出單號起始</span>}>
            <Input
              value={filter.docStart}
              onChange={(e) => handleFilterChange('docStart', e.target.value)}
              placeholder="例：D24001"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="fd160_01">退出單號結束</span>}>
            <Input
              value={filter.docEnd}
              onChange={(e) => handleFilterChange('docEnd', e.target.value)}
              placeholder="例：D24099"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待核准
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認核准 ${selectedKeys.length} 筆繳庫退出單？`}
              onConfirm={batchApprove}
              okText="確認核准"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
            >
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={approving}
                disabled={selectedKeys.length === 0}
              >
                批量核准
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey="doc_no"
        columns={columns}
        dataSource={pendingItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 800 }}
        locale={{ emptyText: '無待核准資料' }}
      />
      <BatchAcceptanceBridge
        manifest={pwd506f1AcceptanceManifest}
        records={pendingItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.doc_no}
        queryExact={async (key) => (await d506Api.getPending({ docStart: key, docEnd: key })).items?.[0] || null}
        lastApiResponse={pendingItems}
        loading={loading || approving}
        onAutoFill={() => pendingItems[0] && setSelectedKeys([pendingItems[0].doc_no])}
        autoFillDisabled={pendingItems.length === 0}
        autoFillReason="只選取正式 API 回傳的第一筆可核准繳庫退出單。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default D506Approval;
